import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LzMCrud from '@lz/models/crud/Crud';
import LzMCrudInput from '@lz/models/crud/Input';
import LzMBase, { LzTBase } from '@lz/models/commons/Base';
import useLzFApiCall from '@lz/services/Api';
import { useLzOnMount } from '@lz/services/Hooks';
import { lzFInitials } from '@lz/services/String';
import { LzTFormSizes } from '@lz/typings/types';
import LzXStaticField from '../forms/fields/StaticField';
import LzXMedia from '../commons/Media';

export interface LzPCrudView {
    crud: LzMCrud;
    modelRef: LzTBase;
    formSize?: LzTFormSizes;
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
}

const LzXCrudView = (props: LzPCrudView) => {
    if (typeof props.crud === 'undefined' || !props.crud || '' === props.crud.ident) {
        return (
            <Alert variant="filled" severity="error">
                CRUD configurations is missing!
            </Alert>
        );
    }

    if (typeof props.modelRef === 'undefined' || !props.modelRef) {
        return (
            <Alert variant="filled" severity="error">
                Model definition is missing!
            </Alert>
        );
    }

    const routeParams = useParams();
    const location = useLocation();

    const [loadedRecord, setLoadedRecord] = useState<LzMBase | null>(null);

    const recordId = +(routeParams.id || 0);

    if (recordId < 1) {
        return (
            <Alert variant="filled" severity="error">
                Record ID is missing!
            </Alert>
        );
    }

    const lzApiCall = useLzFApiCall();

    const loadRecord = async () => {
        // Make the api call and get the data
        const data = await lzApiCall(props.crud.ident + '/' + recordId);

        // Load data if call was successful
        if (data) {
            console.log('CRUD VIEW DATA', data);
            setLoadedRecord(LzMBase.buildModel(data, props.modelRef));
        }
    };

    useLzOnMount(() => {
        loadRecord();
    });

    if (!loadedRecord) {
        return <CircularProgress />;
    }

    const fields = props.crud.viewFormFields;
    const fieldKeys = Object.keys(fields);

    return (
        <React.Fragment>
            {props.children ? (
                props.children
            ) : (
                <div
                    className={
                        'lz-form-page lz-form-page-' +
                        (props.formSize ? props.formSize : 'full') +
                        ' ' +
                        (props.className ? props.className + ' ' : ' ')
                    }
                    style={props.style}
                >
                    <Container>
                        <div className="lz-form">
                            <Grid container spacing={2}>
                                {fieldKeys.map((key, dex) => {
                                    const value = loadedRecord[key];
                                    const field = new LzMCrudInput(fields[key]);

                                    // If its a tree based crud and this is the parent field
                                    if ('parent_id' === key && props.crud.isTreeBased) {
                                        return (
                                            <Grid size={{ sm: field.gridSpanSm, md: field.gridSpanMd }} key={'inputField_' + dex}>
                                                {field.isVisibleFunction(value, loadedRecord, key) && (
                                                    <React.Fragment>
                                                        <LzXStaticField
                                                            fluid
                                                            icon={field.icon}
                                                            label={field.label}
                                                            value={loadedRecord.parent_name}
                                                        />
                                                    </React.Fragment>
                                                )}
                                            </Grid>
                                        );
                                    }

                                    const mediaCalcs = { altField: '', altValue: '', initials: '' };
                                    if (field.isMedia) {
                                        mediaCalcs.altField = field.mediaParams?.altFieldName || '';
                                        mediaCalcs.altValue = '' === mediaCalcs.altField ? '' : loadedRecord[mediaCalcs.altField] || '';
                                        mediaCalcs.initials = '' === mediaCalcs.altValue ? '' : lzFInitials(mediaCalcs.altValue);
                                    }

                                    return (
                                        <Grid size={{ sm: field.gridSpanSm, md: field.gridSpanMd }} key={'inputField_' + dex}>
                                            {field.isVisibleFunction(value, loadedRecord, key) && (
                                                <React.Fragment>
                                                    {field.isMedia ? (
                                                        <LzXMedia
                                                            isAvatar={!!field.mediaParams?.isAvatar}
                                                            avatarSize={field.mediaParams?.avatarSize || 'large'}
                                                            avatarInitials={mediaCalcs.initials}
                                                            altText={mediaCalcs.altValue}
                                                            imgSrc={value ? value.file_preview_path : ''}
                                                            label={field.label}
                                                            className="lz-card-media-small"
                                                        />
                                                    ) : (
                                                        <LzXStaticField
                                                            fluid
                                                            icon={field.icon}
                                                            label={field.label}
                                                            isReactNode={field.isReactNode}
                                                            value={field.displayFunction(value, loadedRecord, key)}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <div style={{ textAlign: 'right' }}>
                                <Link to={props.crud.baseRoute} state={location.state}>
                                    <Button color="primary">Go Back</Button>
                                </Link>
                                <Button color="primary" onClick={() => loadRecord()}>
                                    Reload
                                </Button>
                            </div>
                        </div>
                    </Container>
                </div>
            )}
        </React.Fragment>
    );
};

export default LzXCrudView;
